import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Play, BookOpen, Users, TrendingUp, Search, Clock, ChevronRight, Zap, Shield, BarChart3, Settings, Star, ArrowRight, MessageCircle, Filter, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const KEYFRAMES = `
  @keyframes floatA { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-20px)} }
  @keyframes floatB { 0%,100%{transform:translateY(-8px)} 50%{transform:translateY(10px)} }
  @keyframes rippleOut { 0%{transform:scale(0);opacity:0.45} 100%{transform:scale(5);opacity:0} }
  @keyframes playPulse { 0%,100%{box-shadow:0 0 0 0 rgba(56,254,96,0.4)} 50%{box-shadow:0 0 0 14px rgba(56,254,96,0)} }
  @keyframes shimmer { 0%{background-position:-400% 0} 100%{background-position:400% 0} }
  @keyframes slideTab { 0%{opacity:0;transform:translateX(-6px)} 100%{opacity:1;transform:none} }
  @keyframes fadeUp { 0%{opacity:0;transform:translateY(20px)} 100%{opacity:1;transform:none} }
`;

const THUMB_COLORS = [
  ['#0f2027','#203a43','#2c5364'],
  ['#0d1b2a','#1b2a4a','#162447'],
  ['#0a0a0a','#1a1a2e','#16213e'],
  ['#0f0c29','#302b63','#24243e'],
  ['#1a1a2e','#16213e','#0f3460'],
  ['#0d2137','#1b4f72','#117a8b'],
  ['#1b1b2f','#2e2157','#6a0572'],
  ['#0d1117','#161b22','#21262d'],
  ['#0a1628','#112244','#1a3a5c'],
];

const TUTORIALS = [
  { id: 1, title: 'Getting Started with KiteKraken AI',  desc: 'Set up your account, connect TradingView, and run your first automated strategy in under 10 minutes.', duration: '10 min', category: 'Beginner', level: 0, views: '12.4K', thumb: 0, featured: true  },
  { id: 2, title: 'TradingView Webhook Setup Guide',     desc: 'Complete step-by-step walkthrough to link your TradingView alerts directly to KiteKraken execution.',   duration: '15 min', category: 'Setup',    level: 1, views: '9.8K',  thumb: 1, featured: false },
  { id: 3, title: 'Understanding KiteKraken AI Indicators', desc: 'Deep dive into every indicator in the suite — what they measure, when to use them, and optimal settings.', duration: '22 min', category: 'Beginner', level: 0, views: '8.1K', thumb: 2, featured: false },
];

const CATS = ['All', 'Beginner', 'Setup', 'Advanced'];

const CAT_META = {
  All:      { color: '#38FE60', rgb: '56,254,96' },
  Beginner: { color: '#38FE60', rgb: '56,254,96' },
  Setup:    { color: '#38bdf8', rgb: '56,189,248' },
  Advanced: { color: '#a78bfa', rgb: '167,139,250' },
};

const LEVEL_LABEL = ['Beginner', 'Intermediate', 'Advanced'];

function useVisible(thresh = 0.08) {
  const [v, setV] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setV(true), { threshold: thresh });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, v];
}

function useCounter(target, duration = 1800, started = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    let id;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      setVal(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [target, duration, started]);
  return val;
}

const FadeIn = ({ children, delay = 0, style: extra = {} }) => {
  const [ref, vis] = useVisible();
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : 'translateY(24px)',
      transition: `opacity 0.65s ${delay}ms ease, transform 0.65s ${delay}ms ease`,
      ...extra,
    }}>
      {children}
    </div>
  );
};

const ThumbnailBg = ({ idx, featured }) => {
  const [c1, c2, c3] = THUMB_COLORS[idx % THUMB_COLORS.length];
  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: `linear-gradient(135deg, ${c1} 0%, ${c2} 50%, ${c3} 100%)`,
    }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.18, backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(56,254,96,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 20%, rgba(56,189,248,0.3) 0%, transparent 40%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: featured ? 120 : 60, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }} />
    </div>
  );
};

const PlayBtn = ({ size = 56, color = '#38FE60' }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: `rgba(${color === '#38FE60' ? '56,254,96' : '56,189,248'},0.15)`,
    border: `2px solid ${color}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    animation: 'playPulse 2.6s ease-in-out infinite',
    flexShrink: 0,
  }}>
    <Play style={{ width: size * 0.38, height: size * 0.38, color, marginLeft: 3 }} />
  </div>
);

const TutorialCard = ({ t, index, featured }) => {
  const navigate = useNavigate();
  const [hov, setHov] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [ref, vis] = useVisible(0.06);
  const meta = CAT_META[t.category];

  const addRipple = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const id = Date.now();
    setRipples(r => [...r, { x: e.clientX - rect.left, y: e.clientY - rect.top, id }]);
    setTimeout(() => setRipples(r => r.filter(rp => rp.id !== id)), 700);
  };

  if (featured) {
    return (
      <div ref={ref} style={{
        opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(28px)',
        transition: 'opacity 0.7s ease, transform 0.7s ease',
        borderRadius: 20, overflow: 'hidden',
        border: `1px solid rgba(${meta.rgb},0.3)`,
        background: 'rgba(255,255,255,0.025)',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))',
        cursor: 'pointer',
        boxShadow: hov ? `0 0 40px rgba(${meta.rgb},0.12)` : `0 0 20px rgba(${meta.rgb},0.06)`,
        transform: hov ? 'translateY(-4px)' : vis ? 'none' : 'translateY(28px)',
        transition: 'box-shadow 0.25s, transform 0.25s, opacity 0.7s',
      }}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        onClick={addRipple}
      >
        <div style={{ position: 'relative', minHeight: 240 }}>
          <ThumbnailBg idx={t.thumb} featured />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PlayBtn size={72} color={meta.color} />
          </div>
          <div style={{ position: 'absolute', top: 16, left: 16 }}>
            <span style={{ background: meta.color, color: '#000', fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', padding: '3px 10px', borderRadius: 20 }}>FEATURED</span>
          </div>
          <div style={{ position: 'absolute', bottom: 14, right: 14, background: 'rgba(0,0,0,0.7)', borderRadius: 6, padding: '3px 9px', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Clock style={{ width: 11, height: 11, color: '#aaa' }} />
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{t.duration}</span>
          </div>
        </div>
        <div style={{ padding: '32px 36px', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
          {ripples.map(r => (
            <span key={r.id} style={{ position: 'absolute', left: r.x, top: r.y, width: 10, height: 10, borderRadius: '50%', background: `rgba(${meta.rgb},0.3)`, transform: 'scale(0)', pointerEvents: 'none', animation: 'rippleOut 0.7s ease-out forwards', marginLeft: -5, marginTop: -5 }} />
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ background: `rgba(${meta.rgb},0.12)`, color: meta.color, border: `1px solid rgba(${meta.rgb},0.25)`, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{t.category}</span>
            <span style={{ color: '#555', fontSize: 11 }}>•</span>
            <span style={{ color: '#666', fontSize: 11, fontWeight: 600 }}>{t.views} views</span>
          </div>
          <h2 style={{ color: '#fff', fontSize: 'clamp(1.3rem,2.5vw,1.8rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.25, marginBottom: 14 }}>{t.title}</h2>
          <p style={{ color: '#aaa', fontSize: 14, lineHeight: 1.75, marginBottom: 24, fontWeight: 500 }}>{t.desc}</p>
          <button style={{
            alignSelf: 'flex-start', padding: '11px 26px',
            background: meta.color, color: '#000',
            border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
            fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 7,
            boxShadow: `0 0 24px rgba(${meta.rgb},0.3)`,
            transition: 'box-shadow 0.2s, transform 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 40px rgba(${meta.rgb},0.5)`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 0 24px rgba(${meta.rgb},0.3)`; e.currentTarget.style.transform = 'none'; }}
          >
            Watch Now <ArrowRight style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? (hov ? 'translateY(-5px)' : 'none') : 'translateY(28px)',
      transition: `opacity 0.65s ${index * 60}ms ease, transform 0.25s ease`,
      borderRadius: 16, overflow: 'hidden',
      border: `1px solid ${hov ? `rgba(${meta.rgb},0.35)` : 'rgba(255,255,255,0.07)'}`,
      background: 'rgba(255,255,255,0.025)',
      cursor: 'pointer', display: 'flex', flexDirection: 'column',
      boxShadow: hov ? `0 0 28px rgba(${meta.rgb},0.1)` : 'none',
      transition: 'border 0.2s, box-shadow 0.2s, transform 0.2s',
    }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={addRipple}
    >
      <div style={{ position: 'relative', height: 160, overflow: 'hidden', flexShrink: 0 }}>
        <ThumbnailBg idx={t.thumb} />
        {ripples.map(r => (
          <span key={r.id} style={{ position: 'absolute', left: r.x, top: r.y, width: 10, height: 10, borderRadius: '50%', background: `rgba(${meta.rgb},0.35)`, transform: 'scale(0)', pointerEvents: 'none', animation: 'rippleOut 0.7s ease-out forwards', marginLeft: -5, marginTop: -5, zIndex: 5 }} />
        ))}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ transform: hov ? 'scale(1.12)' : 'scale(1)', transition: 'transform 0.25s' }}>
            <PlayBtn size={48} color={meta.color} />
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(0,0,0,0.72)', borderRadius: 6, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock style={{ width: 10, height: 10, color: '#aaa' }} />
          <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>{t.duration}</span>
        </div>
      </div>
      <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
          <span style={{ background: `rgba(${meta.rgb},0.1)`, color: meta.color, border: `1px solid rgba(${meta.rgb},0.2)`, fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20 }}>{t.category}</span>
          <span style={{ color: '#555', fontSize: 10 }}>•</span>
          <span style={{ color: '#555', fontSize: 10, fontWeight: 600 }}>{t.views} views</span>
        </div>
        <h3 style={{ color: hov ? meta.color : '#fff', fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.4, marginBottom: 8, transition: 'color 0.2s', flex: 1 }}>{t.title}</h3>
        <p style={{ color: '#888', fontSize: 12, lineHeight: 1.65, fontWeight: 500, marginBottom: 16 }}>{t.desc}</p>
        <button style={{
          width: '100%', padding: '10px 0',
          background: `rgba(${meta.rgb},0.08)`,
          color: meta.color,
          border: `1px solid rgba(${meta.rgb},0.25)`,
          borderRadius: 9, fontSize: 12, fontWeight: 700,
          fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          transition: 'background 0.2s, border 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = `rgba(${meta.rgb},0.16)`; e.currentTarget.style.borderColor = `rgba(${meta.rgb},0.5)`; }}
          onMouseLeave={e => { e.currentTarget.style.background = `rgba(${meta.rgb},0.08)`; e.currentTarget.style.borderColor = `rgba(${meta.rgb},0.25)`; }}
        >
          <Play style={{ width: 11, height: 11 }} /> Watch Tutorial
        </button>
      </div>
    </div>
  );
};

const StatItem = ({ target, suffix, label, color, rgb, started }) => {
  const val = useCounter(target, 1600, started);
  return (
    <div style={{ textAlign: 'center', padding: '24px 20px' }}>
      <p style={{ color: '#fff', fontSize: 'clamp(1.6rem,3vw,2.4rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1 }}>{val}{suffix}</p>
      <p style={{ color: '#888', fontSize: 12, fontWeight: 600, marginTop: 6, marginBottom: 10 }}>{label}</p>
      <div style={{ width: 28, height: 3, borderRadius: 2, background: color, boxShadow: `0 0 8px rgba(${rgb},0.6)`, margin: '0 auto' }} />
    </div>
  );
};

const Tutorials = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [statsRef, statsVis] = useVisible(0.1);

  const filtered = useMemo(() => {
    let list = TUTORIALS.filter(t => !t.featured);
    if (activeTab !== 'All') list = list.filter(t => t.category === activeTab);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => t.title.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q) || t.category.toLowerCase().includes(q));
    }
    return list;
  }, [activeTab, search]);

  const featured = TUTORIALS.find(t => t.featured);

  return (
    <div style={{ background: '#111113', minHeight: '100vh', fontFamily: "'Space Grotesk', sans-serif", color: '#fff' }}>
      <style>{KEYFRAMES}</style>

      <section style={{ padding: '100px 24px 64px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)', width: 700, height: 380, background: 'radial-gradient(ellipse, rgba(56,254,96,0.055) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 60, right: '8%', width: 240, height: 240, background: 'radial-gradient(circle, rgba(56,189,248,0.04) 0%, transparent 65%)', pointerEvents: 'none', animation: 'floatA 9s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: 0, left: '6%', width: 180, height: 180, background: 'radial-gradient(circle, rgba(167,139,250,0.04) 0%, transparent 65%)', pointerEvents: 'none', animation: 'floatB 11s ease-in-out infinite' }} />

        <FadeIn>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(56,254,96,0.08)', border: '1px solid rgba(56,254,96,0.18)', borderRadius: 20, padding: '6px 14px', marginBottom: 24 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38FE60', boxShadow: '0 0 8px #38FE60', display: 'inline-block' }} className="animate-pulse" />
            <span style={{ color: '#38FE60', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Learn KiteKraken</span>
          </div>
        </FadeIn>
        <FadeIn delay={60}>
          <h1 style={{ fontSize: 'clamp(2.8rem,7vw,5rem)', fontWeight: 800, letterSpacing: '-0.045em', lineHeight: 1.05, marginBottom: 18, maxWidth: 660, margin: '0 auto 18px' }}>
            Tutorials &<br /><span style={{ color: '#38FE60', textShadow: '0 0 50px rgba(56,254,96,0.25)' }}>Video Guides</span>
          </h1>
        </FadeIn>
        <FadeIn delay={110}>
          <p style={{ color: '#999', fontSize: 17, lineHeight: 1.7, maxWidth: 520, margin: '0 auto 0' }}>
            From first setup to advanced multi-exchange automation — everything you need to master KiteKraken AI.
          </p>
        </FadeIn>
      </section>

      <section style={{ padding: '0 24px 32px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div ref={statsRef} style={{
            borderRadius: 16, border: '1px solid rgba(255,255,255,0.07)',
            background: 'rgba(255,255,255,0.025)',
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0,
            opacity: statsVis ? 1 : 0, transform: statsVis ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}>
            {[
              { target: 9,    suffix: '+',  label: 'Video Tutorials',  color: '#38FE60', rgb: '56,254,96'   },
              { target: 3,    suffix: 'h+', label: 'Hours of Content', color: '#38bdf8', rgb: '56,189,248'  },
              { target: 66,   suffix: 'K+', label: 'Active Students',  color: '#a78bfa', rgb: '167,139,250' },
              { target: 4,    suffix: '.9', label: 'Avg Rating',       color: '#fbbf24', rgb: '251,191,36'  },
            ].map((s, i) => (
              <div key={s.label} style={{ flex: '1 1 140px', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                <StatItem {...s} started={statsVis} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '24px 24px 16px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <FadeIn>
            <TutorialCard t={featured} index={0} featured />
          </FadeIn>
        </div>
      </section>

      <section style={{ padding: '40px 24px 80px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CATS.map(cat => {
                const m = CAT_META[cat];
                const active = activeTab === cat;
                return (
                  <button key={cat} onClick={() => setActiveTab(cat)} style={{
                    padding: '8px 18px',
                    background: active ? `rgba(${m.rgb},0.12)` : 'rgba(255,255,255,0.03)',
                    color: active ? m.color : '#777',
                    border: `1px solid ${active ? `rgba(${m.rgb},0.35)` : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 30, fontSize: 12, fontWeight: 700,
                    fontFamily: "'Space Grotesk', sans-serif",
                    cursor: 'pointer', letterSpacing: '0.02em',
                    transition: 'all 0.2s',
                  }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.color = '#ccc'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; } }}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.color = '#777'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; } }}
                  >
                    {cat}
                    <span style={{ marginLeft: 6, background: active ? `rgba(${m.rgb},0.2)` : 'rgba(255,255,255,0.06)', color: active ? m.color : '#555', borderRadius: 10, padding: '0 6px', fontSize: 10, fontWeight: 800 }}>
                      {cat === 'All' ? TUTORIALS.filter(t => !t.featured).length : TUTORIALS.filter(t => t.category === cat && !t.featured).length}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={{ position: 'relative', minWidth: 220 }}>
              <Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#555', pointerEvents: 'none' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tutorials..."
                style={{
                  width: '100%', padding: '9px 36px 9px 34px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 30, color: '#fff', fontSize: 12, fontWeight: 500,
                  fontFamily: "'Space Grotesk', sans-serif",
                  outline: 'none', boxSizing: 'border-box',
                  transition: 'border 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(56,254,96,0.4)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                  <X style={{ width: 13, height: 13, color: '#555' }} />
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#555' }}>
              <Search style={{ width: 32, height: 32, margin: '0 auto 12px', display: 'block', opacity: 0.4 }} />
              <p style={{ fontSize: 15, fontWeight: 600 }}>No tutorials found for "{search}"</p>
              <button onClick={() => { setSearch(''); setActiveTab('All'); }} style={{ marginTop: 14, background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, padding: '8px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif" }}>
                Clear filters
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {filtered.map((t, i) => (
                <TutorialCard key={t.id} t={t} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section style={{ padding: '0 24px 100px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#38FE60', display: 'inline-block' }} />
                <span style={{ color: '#38FE60', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Need More Help?</span>
              </div>
              <h2 style={{ fontSize: 'clamp(1.8rem,4vw,2.8rem)', fontWeight: 800, letterSpacing: '-0.04em' }}>More Ways to Learn</h2>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))', gap: 12, alignItems: 'stretch' }}>
            {[
              { icon: MessageCircle, color: '#38FE60', rgb: '56,254,96',    title: 'VIP Telegram',      desc: '30+ expert analysts posting live trade ideas daily.',     btn: 'Join Community', href: 'https://t.me/bullexailabs' },
              { icon: BookOpen,      color: '#38bdf8', rgb: '56,189,248',   title: 'Documentation',     desc: 'Full API reference, indicator guides, and setup docs.',    btn: 'View Docs',      path: '/documentation' },
              { icon: Shield,        color: '#a78bfa', rgb: '167,139,250',  title: '24/7 Support',      desc: 'Live chat and email — average response under 2 minutes.', btn: 'Get Help',       path: '/contact' },
            ].map(({ icon: Icon, color, rgb, title, desc, btn, href, path }) => (
              <FadeIn key={title} style={{ display: 'flex' }}>
                <div
                  style={{
                    borderRadius: 16, padding: '32px 28px',
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center',
                    flex: 1, width: '100%',
                    transition: 'border 0.2s, transform 0.2s, box-shadow 0.2s',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.border = `1px solid rgba(${rgb},0.3)`; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 0 24px rgba(${rgb},0.08)`; }}
                  onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: `rgba(${rgb},0.1)`, border: `1px solid rgba(${rgb},0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <Icon style={{ width: 22, height: 22, color }} />
                  </div>
                  <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>{title}</h3>
                  <p style={{ color: '#aaa', fontSize: 14, lineHeight: 1.7, fontWeight: 500, marginBottom: 24, flex: 1 }}>{desc}</p>
                  <button
                    onClick={() => href ? window.open(href, '_blank') : navigate(path)}
                    style={{
                      padding: '10px 22px', background: `rgba(${rgb},0.1)`, color,
                      border: `1px solid rgba(${rgb},0.25)`,
                      borderRadius: 9, fontSize: 12, fontWeight: 700,
                      fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                      transition: 'background 0.2s, border 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `rgba(${rgb},0.18)`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `rgba(${rgb},0.1)`; }}
                  >
                    {btn} <ArrowRight style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tutorials;
